/**
 * Module dependencies.
 */

import { Container } from 'src/components/core/layout/container';
import { Image } from 'src/components/core/image';
import { Richtext } from 'src/components/core/richtext';
import { Text } from 'src/components/core/text';
import { media } from 'src/styles/utils/media';
import { useBreakpoint } from '@untile/react-components';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  blocks: {
    description?: string;
    title?: string;
  }[];
  className?: string;
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  overflow-x: clip;
  position: relative;
`;

/**
 * `BlurImage` styled component.
 */

const BlurImage = styled(Image)`
  transform: translate(0, -10%);
`;

/**
 * `Row` styled component.
 */

const Row = styled.div`
  margin-bottom: 3rem;
  text-align: center;

  ${media.min.md`
    margin-bottom: var(--vertical-spacing);
    margin-left: 20%;
    width: 60%;
  `}
`;

/**
 * Export `VerticalTextBlocks` component.
 */

export function VerticalTextBlocks({ blocks, ...rest }: Props) {
  const isDesktop = useBreakpoint('md');
  const normalizedBlocks = blocks.filter(({ description, title }) => {
    return !!description && !!title;
  }) as Required<Props['blocks']>;

  if (!normalizedBlocks?.length) {
    return null;
  }

  return (
    <Section {...rest}>
      {isDesktop && (
        <BlurImage
          alt={'stain'}
          fill
          objectFit={'contain'}
          objectPosition={'bottom left'}
          src={'/images/bg-left.png'}
        />
      )}

      <Container>
        {normalizedBlocks?.map(({ description, title }, index) => (
          <Row key={index}>
            <Text
              block
              data-scroll={'fade-in-up'}
              data-scroll-offset={'20%'}
              style={{ marginBottom: '2.5rem' }}
              variant={'h2'}
            >
              {title}
            </Text>

            <div
              data-scroll={'fade-in-up'}
              data-scroll-animation-delay={100}
              data-scroll-offset={'20%'}
            >
              <Richtext>{description}</Richtext>
            </div>
          </Row>
        ))}
      </Container>
    </Section>
  );
}
